import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiService, OmniChannelStats, FlowSessionsResponse, AssignmentStats, DashboardCampaign, BusinessUser, WhatsAppCustomTemplate } from '../../services/api';
import { useAppStore } from '../../store/appStore';
import { DateRangePreset } from './DashboardToolbar';

export interface DashboardData {
  stats: OmniChannelStats | null;
  flowSessions: FlowSessionsResponse | null;
  assignmentStats: AssignmentStats | null;
  campaigns: DashboardCampaign[];
  users: BusinessUser[];
  templates: WhatsAppCustomTemplate[];
}

export interface UseDashboardDataResult extends DashboardData {
  loading: boolean;
  error: string | null;
  refresh: () => void;
  dateRange: DateRangePreset;
  setDateRange: (preset: DateRangePreset) => void;
}

function presetToStartDate(preset: DateRangePreset): Date {
  const now = new Date();
  switch (preset) {
    case 'today':
      return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    case '7d':
      return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    case '90d':
      return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    case '30d':
    default:
      return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  }
}

/** Plain fetch hook, not a Zustand store — this data is dashboard-local and
 * doesn't need to be shared/persisted across the app, consistent with there
 * being no existing analytics-store convention in this codebase (see the
 * Session 1 plan's reasoning). Fetches all four sources in parallel via
 * Promise.all since they're independent of each other. */
export function useDashboardData(): UseDashboardDataResult {
  const { currentBusiness } = useAppStore();
  const [data, setData] = useState<DashboardData>({
    stats: null,
    flowSessions: null,
    assignmentStats: null,
    campaigns: [],
    users: [],
    templates: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [dateRange, setDateRange] = useState<DateRangePreset>('30d');

  const refresh = useCallback(() => setRefreshToken((t) => t + 1), []);

  const startDate = useMemo(() => presetToStartDate(dateRange), [dateRange]);
  const businessId = currentBusiness?._id;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [statsRes, flowSessionsRes, assignmentStatsRes, campaignsRes, usersRes, templatesRes] = await Promise.all([
          apiService.getOmniChannelStats(startDate),
          apiService.getFlowSessions({ status: 'active', limit: 10 }),
          apiService.getAssignmentStats(),
          apiService.getLatestCampaigns(10),
          businessId ? apiService.getBusinessUsers(businessId) : Promise.resolve({ data: [] } as any),
          apiService.getWhatsAppCustomTemplates(),
        ]);

        if (cancelled) return;

        setData({
          stats: statsRes.data ?? null,
          flowSessions: flowSessionsRes.data ?? null,
          assignmentStats: assignmentStatsRes.data ?? null,
          campaigns: campaignsRes.data ?? [],
          users: usersRes.data ?? [],
          templates: (Array.isArray(templatesRes.data)
            ? templatesRes.data
            : templatesRes.data?.templates) ?? [],
        });

        // Individual sources failing (e.g. one endpoint erroring) shouldn't
        // blank the whole dashboard — each already degrades to null/[] above.
        // Only surface a banner if EVERY source failed, which usually means
        // an auth/network problem rather than one endpoint being empty.
        const allFailed = !statsRes.data && !flowSessionsRes.data && !assignmentStatsRes.data && campaignsRes.data == null;
        if (allFailed) setError('Unable to load dashboard data.');
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Unable to load dashboard data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [refreshToken, startDate, businessId]);

  return { ...data, loading, error, refresh, dateRange, setDateRange };
}
